import { SuggestionData } from 'ckeditor5-premium-features'
import { ObjectBuilder } from 'components/WorkspaceEditor/factories'
import {
    SuggestionDataMap,
    useSuggestionsShallowSelector,
} from 'components/WorkspaceEditor/SuggestionStore'
import {
    TrackChangeStatus,
    TrackChangeType,
    TrackedChange,
} from 'models/changes'
import { useCallback, useMemo } from 'react'
import {
    buildSuggestionArray,
    buildSuggestionRecord,
} from '../utils/TrackChangesAndSuggestionsUtils'

export const CHANGE_TYPE_MAP = {
    [TrackChangeType.ADDED]: 'insertion',
    [TrackChangeType.DELETED]: 'deletion',
    [TrackChangeType.MODIFIED]: 'modification',
    [TrackChangeType.REPLACED]: 'replaced',
    [TrackChangeType.DISABLED]: 'disabled',
} as const

export const useSuggestionDataHandler = () => {
    const {
        trackChangesData,
        addOrUpdateSuggestions,
    } = useSuggestionsShallowSelector((state) => ({
        trackChangesData: state.trackChangesData,
        addOrUpdateSuggestions: state.addOrUpdateSuggestions,
    }))

    const toSuggestionData = useCallback(
        (value: TrackedChange, from?: string): SuggestionData => {
            const type =
                from === undefined
                    ? value.status
                    : CHANGE_TYPE_MAP[
                          value.changeType as keyof typeof CHANGE_TYPE_MAP
                      ] || value.changeType

            const trackChangeTypeToChangedTexts =
                (value.changeType === TrackChangeType.OPTIONGROUP &&
                    value.trackChangeTypeToChangedTexts?.map(
                        ({ trackChangeType, changedText }) => ({
                            trackChangeType,
                            changedText,
                        })
                    )) ||
                null
            const suggestionId = value.id
            const modifiedDateTime =
                from === TrackChangeStatus.PENDING
                    ? value.changeDateTime
                    : value.approvedOrRejectedDateTime
            const approvedOrRejectedDateTime =
                value?.approvedOrRejectedDateTime !== null
                    ? value.approvedOrRejectedDateTime
                    : undefined

            return new ObjectBuilder<SuggestionData>()
                .set('id', suggestionId)
                .set('type', type)
                .set('authorId', '')
                .set('attributes', {
                    trackChangeTypeToChangedTexts,
                    approvedOrRejectedDateTime,
                })
                .set(
                    'createdAt',
                    modifiedDateTime ? new Date(modifiedDateTime) : undefined
                )
                .set('data', {
                    key: value.contentItemElementId ?? '',
                    oldValue: value.changedText,
                    newValue: value.changedText,
                })
                .build()
        },
        []
    )

    const groupedChanges = useMemo(() => {
        const groups: Record<string, TrackedChange[]> = {}
        Object.values(trackChangesData).forEach((change) => {
            const status = change.status.toLowerCase()
            groups[status] = groups[status] || []
            groups[status].push(change)
        })
        return groups
    }, [trackChangesData])

    const buildSuggestions = useCallback(
        (
            changes: TrackedChange[],
            from?: string
        ): Record<string, SuggestionDataMap> => {
            const suggestionArray = buildSuggestionArray(
                changes,
                toSuggestionData,
                from
            )
            return buildSuggestionRecord(suggestionArray)
        },
        [toSuggestionData]
    )

    const renderSuggestions = useCallback(
        (statuses: string[], from?: string) => {
            const allChanges = statuses.flatMap((status) => {
                const normalizedStatus = status.toLowerCase()
                const statusChanges = groupedChanges[normalizedStatus] || []

                return statusChanges
            })

            const suggestionRecord = buildSuggestions(allChanges, from)
            addOrUpdateSuggestions(suggestionRecord, 'replace')
        },
        [groupedChanges, buildSuggestions, addOrUpdateSuggestions]
    )

    return { renderSuggestions, buildSuggestions }
}
